import { DatePickerDay } from './date-picker-calendar-day';
import { calendarClasses } from './date-picker-calendar.styles';
import {
  areDatesEqual,
  getDaysToAppend,
  getLastDateOfMonth,
  getLastDateOfPreviousMonth,
  getLastDayOfMonth,
  getMonthName,
  getNextMonth,
  getPreviousMonth,
} from './date-picker-utilities';
import { CalendarMenuType, DateMenuProps, DatePickerActionType } from './date-picker.types';
import { ChevronLeftIcon, ChevronRightIcon } from '../..';

const getNewSelection = (
  date: Date,
  isRangeSelection: boolean,
  selectionStart?: Date,
  selectionEnd?: Date
): [Date?, Date?] => {
  const isCurrentDate = selectionStart && areDatesEqual(date, selectionStart);

  // Simple case, single date selection
  if (!isRangeSelection) {
    return [isCurrentDate ? undefined : date, undefined];
  }

  // Range selection
  if (selectionStart && selectionEnd) {
    // If there is already a selection, clear the current selection and start over
    return [date, undefined];
  } else if (isCurrentDate && selectionEnd === undefined) {
    // Clicking on start when end date is not set should select end date
    selectionEnd = selectionStart;
  } else if (selectionStart === undefined) {
    // If selection start isn't set, then set it
    selectionStart = date;
  } else {
    selectionEnd = date;
  }

  // Fix order of dates
  if (selectionEnd && selectionStart && selectionStart > selectionEnd) {
    return [selectionEnd, selectionStart];
  }

  return [selectionStart, selectionEnd];
};

// eslint-disable-next-line max-lines-per-function
export const DateMenu = (props: DateMenuProps) => {
  const { state, dispatch, onChange } = props;
  const { year, month, selectionStart, selectionEnd, isRangeSelection } = state;
  const startDayOfMonth = new Date(year, month).getDay();
  const lastDateOfMonth = getLastDateOfMonth(year, month);
  const lastDayOfMonth = getLastDayOfMonth(year, month);
  const lastDateOfPrevMonth = getLastDateOfPreviousMonth(year, month);

  const dayArgs = {
    toggleSelection: (date: Date) => {
      const selection = getNewSelection(date, isRangeSelection, selectionStart, selectionEnd);
      dispatch({ type: DatePickerActionType.SELECT_DATE, payload: selection });
      onChange(selection);
    },
    selectionStart,
    selectionEnd,
    isRangeSelection,
  };
  const days = [];
  const [prevMonthYear, prevMonth] = getPreviousMonth(year, month);
  for (let d = lastDateOfPrevMonth - startDayOfMonth + 1; d <= lastDateOfPrevMonth; ++d) {
    const date = new Date(prevMonthYear, prevMonth, d);
    const isDisabled = props.isDisabledDate?.(date) ?? false;
    days.push(
      <DatePickerDay key={date.toString()} {...dayArgs} date={date} isCurrentMonth={false} isDisabled={isDisabled} />
    );
  }
  for (let d = 1; d <= lastDateOfMonth; ++d) {
    const date = new Date(year, month, d);
    const isDisabled = props.isDisabledDate?.(date) ?? false;
    days.push(
      <DatePickerDay key={date.toString()} {...dayArgs} date={date} isCurrentMonth={true} isDisabled={isDisabled} />
    );
  }
  const [nextMonthYear, nextMonth] = getNextMonth(year, month);
  const daysToAppend = getDaysToAppend(year, month);
  for (let d = 1; d <= daysToAppend - lastDayOfMonth; ++d) {
    const date = new Date(nextMonthYear, nextMonth, d);
    const isDisabled = props.isDisabledDate?.(date) ?? false;
    days.push(
      <DatePickerDay key={date.toString()} {...dayArgs} date={date} isCurrentMonth={false} isDisabled={isDisabled} />
    );
  }

  return (
    <div className={calendarClasses.containerClasses}>
      <div className={calendarClasses.headerClasses}>
        <div className="flex">
          <div className={calendarClasses.monthTextClasses}>
            <button
              title="year menu"
              className={calendarClasses.headerButtonClasses}
              onClick={() => dispatch({ type: DatePickerActionType.SET_MENU, menu: CalendarMenuType.YEAR_MENU })}
            >
              {year}
            </button>{' '}
            <button
              title="month menu"
              className={calendarClasses.headerButtonClasses}
              onClick={() => dispatch({ type: DatePickerActionType.SET_MENU, menu: CalendarMenuType.MONTH_MENU })}
            >
              {getMonthName(month)}
            </button>
          </div>
          <button
            title="prev month"
            className={calendarClasses.monthButtonClasses}
            onClick={() => dispatch({ type: DatePickerActionType.PREV_MONTH })}
          >
            <ChevronLeftIcon className={calendarClasses.monthButtonIconClasses} />
          </button>
          <button
            title="next month"
            className={calendarClasses.monthButtonClasses}
            onClick={() => dispatch({ type: DatePickerActionType.NEXT_MONTH })}
          >
            <ChevronRightIcon className={calendarClasses.monthButtonIconClasses} />
          </button>
        </div>
      </div>
      <div className={calendarClasses.dayGridClasses}>
        <div className={calendarClasses.rowHeaderClasses}>Su</div>
        <div className={calendarClasses.rowHeaderClasses}>Mo</div>
        <div className={calendarClasses.rowHeaderClasses}>Tu</div>
        <div className={calendarClasses.rowHeaderClasses}>We</div>
        <div className={calendarClasses.rowHeaderClasses}>Th</div>
        <div className={calendarClasses.rowHeaderClasses}>Fr</div>
        <div className={calendarClasses.rowHeaderClasses}>Sa</div>
        {days}
      </div>
    </div>
  );
};
